import axios from 'axios';
import { toast } from 'react-toastify';

// Create axios instance with improved configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5002/api',
  timeout: 30000, // Increased from 15s to 30s
  headers: {
    'Content-Type': 'application/json'
  },
  // Add retry configuration
  retry: 5, // Increased from 3 to 5
  retryDelay: 1000,
  // Prevent connection reuse issues
  maxContentLength: 50 * 1024 * 1024, // 50MB
  maxBodyLength: 50 * 1024 * 1024, // 50MB
  // Prevent CORS issues
  withCredentials: true
});

// Retry logic helper with improved backoff strategy
// Suppresses interceptor toast errors during retries; only shows final error
const retryRequest = async (config, retryCount = 0) => {
  try {
    // Mark config so the response interceptor knows we may retry and should suppress toasts
    const configWithRetry = { ...config, _retryCount: retryCount, _suppressToast: true };
    return await api.request(configWithRetry);
  } catch (error) {
    const maxRetries = config.retry ?? 5;
    const delay = config.retryDelay || 1000;
    
    // Enhanced retry logic with exponential backoff
    const isNetworkError = !error.response || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || error.message?.includes('Network Error');
    const isRetryableStatus = error.response && error.response.status >= 500;
    const shouldRetry = retryCount < maxRetries && (isNetworkError || isRetryableStatus);
    
    // Log retry attempts for PDF requests
    if (config.url?.includes('/pdf')) {
      console.log(`[API RETRY] PDF request failed: ${error.response?.status || 'Network Error'}`);
      console.log(`[API RETRY] Retry count: ${retryCount}/${maxRetries}`);
      console.log(`[API RETRY] Should retry: ${shouldRetry}`);
      console.log(`[API RETRY] Timestamp: ${new Date().toISOString()}`);
    }
    
    if (shouldRetry) {
      // Exponential backoff with jitter
      const backoffTime = delay * Math.pow(2, retryCount) + Math.random() * 1000;
      console.log(`Retrying request (${retryCount + 1}/${maxRetries}) after ${backoffTime.toFixed(0)}ms...`);
      await new Promise(resolve => setTimeout(resolve, backoffTime));
      return retryRequest(config, retryCount + 1);
    }
    
    // All retries exhausted — show the user-facing error now
    // Re-throw without _suppressToast so the interceptor shows the toast once
    delete error.config?._suppressToast;
    throw error;
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, config.data);
    }
    
    // Log PDF generation requests specifically
    if (config.url?.includes('/pdf')) {
      console.log(`[API] PDF Request: ${config.method?.toUpperCase()} ${config.url}`);
      console.log(`[API] Timestamp: ${new Date().toISOString()}`);
    }
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    }
    
    return response;
  },
  (error) => {
    // Check if this error should suppress toast (during retries)
    const suppressToast = error.config?._suppressToast;
    
    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error.response?.data);
    }
    
    // Handle different error scenarios - only show toast if not suppressed (not in retry cycle)
    if (error.response) {
      const { status, data } = error.response;
      
      if (!suppressToast) {
        switch (status) {
          case 401:
            // Unauthorized - redirect to login
            if (window.location.pathname !== '/login') {
              localStorage.removeItem('token');
              delete api.defaults.headers.common['Authorization'];
              toast.error('Session expired. Please login again.');
            }
            break;
            
          case 403:
            toast.error(data.message || 'Access denied');
            break;
            
          case 404:
            // Not found
            if (!error.config.url?.includes('/auth/me')) {
              toast.error(data.message || 'Resource not found');
            }
            break;
            
          case 422:
            // Validation error
            if (data.errors && Array.isArray(data.errors)) {
              data.errors.forEach(err => {
                toast.error(err.msg || err.message);
              });
            } else {
              toast.error(data.message || 'Validation error');
            }
            break;
            
          case 429:
            // Rate limit exceeded
            toast.error('Too many requests. Please try again later.');
            break;
            
          case 500:
            // Server error
            toast.error(data.message || 'Internal server error');
            break;
            
          default:
            // Other errors
            toast.error(data.message || `Error ${status}`);
        }
      }
    } else if (error.request) {
      // Network error - more specific handling
      if (!suppressToast) {
        if (error.code === 'ECONNABORTED') {
          toast.error('Request timeout. The server is taking too long to respond.');
        } else if (error.code === 'ERR_NETWORK' || error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
          toast.error('Network connection failed. Please check your internet connection and server status.');
        } else {
          toast.error('Unable to connect to server. Please try again later.');
        }
      }
      console.error('Network Error Details:', {
        code: error.error,
        message: error.message,
        url: error.config?.url,
        suppressToast: !!suppressToast
      });
    } else {
      // Other error
      if (!suppressToast) {
        toast.error('An unexpected error occurred.');
      }
      console.error('Unexpected Error:', error);
    }
    
    return Promise.reject(error);
});

// Enhanced HTTP methods with retry logic
const enhancedAPI = {
  get: (url, config = {}) => retryRequest({ ...config, method: 'get', url }),
  post: (url, data, config = {}) => retryRequest({ ...config, method: 'post', url, data }),
  put: (url, data, config = {}) => retryRequest({ ...config, method: 'put', url, data }),
  delete: (url, config = {}) => retryRequest({ ...config, method: 'delete', url }),
  patch: (url, data, config = {}) => retryRequest({ ...config, method: 'patch', url, data })
};

// API methods
export const authAPI = {
  login: (credentials) => enhancedAPI.post('/auth/login', credentials),
  register: (userData) => enhancedAPI.post('/auth/register', userData),
  forgotPassword: (email) => enhancedAPI.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => enhancedAPI.post('/auth/reset-password', { token, password }),
  verifyToken: (token) => enhancedAPI.post('/auth/verify-token', { token }),
  getProfile: () => enhancedAPI.get('/auth/me'),
  updateProfile: (userData) => enhancedAPI.put('/auth/profile', userData),
  changePassword: (passwordData) => enhancedAPI.put('/auth/password', passwordData),
  refreshToken: () => enhancedAPI.post('/auth/refresh-token'),
  // User management methods (Admin only)
  getUsers: (params) => enhancedAPI.get('/auth/users', { params }),
  updateUser: (id, userData) => enhancedAPI.put(`/auth/users/${id}`, userData),
  toggleUserStatus: (id) => enhancedAPI.put(`/auth/users/${id}/toggle-status`),
  deleteUser: (id) => enhancedAPI.delete(`/auth/users/${id}`)
};

export const customersAPI = {
  getAll: (params) => enhancedAPI.get('/customers', { params }),
  getCustomers: (params) => enhancedAPI.get('/customers', { params }),
  getById: (id) => enhancedAPI.get(`/customers/${id}`),
  create: (customerData) => enhancedAPI.post('/customers', customerData),
  update: (id, customerData) => enhancedAPI.put(`/customers/${id}`, customerData),
  delete: (id) => enhancedAPI.delete(`/customers/${id}`),
  search: (query) => enhancedAPI.get(`/customers/search?q=${encodeURIComponent(query)}`)
};

export const proposalsAPI = {
  getAll: (params) => enhancedAPI.get('/proposals', { params }),
  getById: (id) => enhancedAPI.get(`/proposals/${id}`),
  create: (proposalData) => enhancedAPI.post('/proposals', proposalData),
  update: (id, proposalData) => enhancedAPI.put(`/proposals/${id}`, proposalData),
  delete: (id) => enhancedAPI.delete(`/proposals/${id}`),
  updateStatus: (id, status) => enhancedAPI.put(`/proposals/${id}/status`, { status }),
  getStats: () => enhancedAPI.get('/proposals/stats/dashboard'),
  duplicate: (id) => enhancedAPI.post(`/proposals/${id}/duplicate`),
  // PDF generation endpoints
  generatePDF: (id) => enhancedAPI.post(`/proposals/${id}/pdf`),
  downloadPDF: (id) => enhancedAPI.get(`/proposals/${id}/pdf/download`, { responseType: 'blob' }),
  regeneratePDF: (id) => enhancedAPI.post(`/proposals/${id}/pdf/regenerate`),
  deletePDF: (id) => enhancedAPI.delete(`/proposals/${id}/pdf`)
};

export const templatesAPI = {
  getProposalTemplates: (params) => enhancedAPI.get('/templates/proposals', { params }),
  getEmailTemplates: (params) => enhancedAPI.get('/templates/emails', { params }),
  getProposalTemplate: (id) => enhancedAPI.get(`/templates/proposals/${id}`),
  getEmailTemplate: (id) => enhancedAPI.get(`/templates/emails/${id}`),
  createProposalTemplate: (templateData) => enhancedAPI.post('/templates/proposals', templateData),
  createEmailTemplate: (templateData) => enhancedAPI.post('/templates/emails', templateData),
  updateProposalTemplate: (id, templateData) => enhancedAPI.put(`/templates/proposals/${id}`, templateData),
  updateEmailTemplate: (id, templateData) => enhancedAPI.put(`/templates/emails/${id}`, templateData),
  deleteProposalTemplate: (id) => enhancedAPI.delete(`/templates/proposals/${id}`),
  deleteEmailTemplate: (id) => enhancedAPI.delete(`/templates/emails/${id}`)
};

export const emailsAPI = {
  sendProposal: (proposalId, emailData) => enhancedAPI.post('/emails/send-proposal', { proposalId, ...emailData }),
  testSmtp: (smtpData) => enhancedAPI.post('/emails/test-smtp', smtpData),
  sendTestEmail: (emailData) => enhancedAPI.post('/emails/test', emailData),
  sendReminder: (proposalId) => enhancedAPI.post(`/emails/reminder/${proposalId}`),
  getDefaultTemplate: (type) => enhancedAPI.get(`/emails/default-template/${type}`)
};

export const settingsAPI = {
  get: () => enhancedAPI.get('/settings'),
  updateCompany: (companyData) => enhancedAPI.put('/settings/company', companyData),
  updateSmtp: (smtpData) => enhancedAPI.put('/settings/smtp', smtpData),
  updateApplication: (appData) => enhancedAPI.put('/settings/application', appData),
  uploadLogo: (logoFile) => {
    const formData = new FormData();
    formData.append('logo', logoFile);
    return enhancedAPI.post('/settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  removeLogo: () => enhancedAPI.delete('/settings/logo'),
  testEmailSettings: (emailData) => enhancedAPI.post('/emails/send-test', emailData),
  exportBackup: () => enhancedAPI.get('/settings/backup', { responseType: 'blob' }),
  importBackup: (backupData) => enhancedAPI.post('/settings/restore', { backupData })
};

export const signaturesAPI = {
  addCompanySignature: (proposalId, signatureData) => 
    enhancedAPI.post(`/signatures/company/${proposalId}`, signatureData),
  uploadCompanySignature: (proposalId, signatureFile, signerName) => {
    const formData = new FormData();
    formData.append('signature', signatureFile);
    formData.append('signerName', signerName);
    return enhancedAPI.post(`/signatures/company/${proposalId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  addCustomerSignature: (proposalId, signatureData) => 
    enhancedAPI.post(`/signatures/customer/${proposalId}`, signatureData),
  getSignatureInfo: (proposalId) => enhancedAPI.get(`/signatures/proposal/${proposalId}`),
  removeCompanySignature: (proposalId) => enhancedAPI.delete(`/signatures/company/${proposalId}`),
  getStats: (params) => enhancedAPI.get('/signatures/stats', { params })
};

export const trackingAPI = {
  getStats: (params) => enhancedAPI.get('/tracking/stats', { params }),
  getEvents: (proposalId) => enhancedAPI.get(`/tracking/events/${proposalId}`),
  getProposal: (proposalId) => enhancedAPI.get(`/tracking/proposal/${proposalId}`)
};

// File upload helper
export const uploadFile = async (file, endpoint, onProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  
  if (onProgress) {
    config.onUploadProgress = (progressEvent) => {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
      onProgress(percentCompleted);
    };
  }
  
  return enhancedAPI.post(endpoint, formData, config);
};

// Download helper
export const downloadFile = async (url, filename) => {
  try {
    const response = await enhancedAPI.get(url, {
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
    
    return { success: true };
  } catch (error) {
    console.error('Download error:', error);
    return { success: false, error: error.message };
  }
};

// Health check with retry
export const healthCheck = () => enhancedAPI.get('/health', { retry: 5, retryDelay: 500 });

// Notifications API
export const notificationsAPI = {
  getAll: (params) => enhancedAPI.get('/notifications', { params }),
  getCount: () => enhancedAPI.get('/notifications/count'),
  getRecent: (limit = 5) => enhancedAPI.get(`/notifications/recent?limit=${limit}`),
  markAsRead: (id) => enhancedAPI.put(`/notifications/${id}/read`),
  markAllAsRead: () => enhancedAPI.put('/notifications/read-all'),
  deleteAll: () => enhancedAPI.delete('/notifications/delete-all'),
  delete: (id) => enhancedAPI.delete(`/notifications/${id}`)
};

// Combined API object
const combinedAPI = {
  // HTTP methods with retry logic
  get: enhancedAPI.get,
  post: enhancedAPI.post,
  put: enhancedAPI.put,
  delete: enhancedAPI.delete,
  patch: enhancedAPI.patch,
  // API modules
  auth: authAPI,
  customers: customersAPI,
  proposals: proposalsAPI,
  templates: templatesAPI,
  emails: emailsAPI,
  settings: settingsAPI,
  signatures: signaturesAPI,
  tracking: trackingAPI,
  notifications: notificationsAPI,
  healthCheck,
  // Expose the raw axios instance for direct access to defaults, interceptors, etc.
  defaults: api.defaults,
  interceptors: api.interceptors
};

export default combinedAPI;